import { useEffect, useMemo, useState } from 'react';
import { Surface, eyebrowStyle, ghostLinkStyle, inputStyle, pageStyle } from '@kilat/ui';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet, type OutletLite } from '../lib/use-active-outlet.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { logger } from '../lib/logger.js';
import {
  LOW_PRICE_FLOOR_SEN,
  flagLowPrices,
  type LowPriceEntry,
  type LowPriceFlag,
} from '../lib/low-price-check.js';
import {
  addOnsRepoFor,
  packagesRepoFor,
  servicesRepoFor,
  vehicleTypesRepoFor,
} from '../lib/repositories/outlet-repos.js';
import {
  excludedOutletIdsFromMap,
  money,
  outletAssignmentMap,
  type AddOn,
  type Package,
  type Service,
  type VehicleType,
} from '@kilat/shared';

/**
 * /catalog — operator-editable services / packages / add-ons / vehicle
 * types. Seeded defaults at tenant signup come from
 * `@kilat/config-seed/packages.ts`; this page lets the operator
 * customise names, pricing per vehicle type, and active status.
 *
 * MVP scope (Sprint 53):
 *   - View all four collections
 *   - Inline-edit existing rows (name EN, prices by vehicle type, active toggle)
 *   - No add / delete (operators have seeded defaults; rare to need a brand-new
 *     service/package mid-business — defer add/delete to a later sprint when
 *     the operator workflow asks for it)
 *   - Edit EN string only; existing MS/ZH/TA values preserved untouched
 *     so a polyglot brand keeps its multi-locale labels across edits
 *
 * Per [feedback-no-dead-ends]: every per-row state explicit (viewing /
 * editing / saving / saved / failed) with copy + next-action.
 */

type Tab = 'services' | 'packages' | 'addOns' | 'vehicleTypes';

const TABS: readonly { id: Tab }[] = [
  { id: 'packages' },
  { id: 'services' },
  { id: 'addOns' },
  { id: 'vehicleTypes' },
];

export function CatalogPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const { outletId, outlets, loading: outletLoading } = useActiveOutlet();
  const [tab, setTab] = useState<Tab>('packages');

  if (authLoading || outletLoading) return <Splash label={t.t('dashboard.catalog.loading')} />;
  if (!user) return <SignedOut />;
  if (!tenantId) return <Splash label={t.t('dashboard.states.loadingAccount')} />;
  // Sprint 106 — Every catalog tab is tenant-scoped. No tab needs an active
  // outlet. The OutletSwitcher above stays for queue/POS context but the
  // catalog editor is now outlet-agnostic.
  // The unused `outletId` warning is silenced — kept in scope for future
  // per-outlet preview affordances inside specific tabs.
  void outletId;

  return (
    <main style={dashboardPageStyle}>
      <Header activeTab={tab} setTab={setTab} />
      <section
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: '0 var(--space-5) var(--space-12) var(--space-5)',
        }}
      >
        {tab === 'packages' && <PackagesTab tenantId={tenantId} outlets={outlets} />}
        {tab === 'services' && <ServicesTab tenantId={tenantId} outlets={outlets} />}
        {tab === 'addOns' && <AddOnsTab tenantId={tenantId} outlets={outlets} />}
        {tab === 'vehicleTypes' && <VehicleTypesTab tenantId={tenantId} outlets={outlets} />}
      </section>
    </main>
  );
}

// ── header / shell ─────────────────────────────────────────────────────

function Header({
  activeTab,
  setTab,
}: {
  readonly activeTab: Tab;
  readonly setTab: (next: Tab) => void;
}) {
  const t = useI18n();
  return (
    <header
      style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        marginBottom: 'var(--space-6)',
      }}
    >
      <div
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: 'var(--space-5)',
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        <div>
          <p style={eyebrowStyle}>{t.t('dashboard.catalog.eyebrow')}</p>
          <h1
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            {t.t('dashboard.catalog.title')}
          </h1>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}
        >
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.states.backToQueue')}
          </Link>
        </div>
      </div>
      <nav
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: '0 var(--space-5)',
          display: 'flex',
          gap: 'var(--space-2)',
          paddingBottom: 'var(--space-3)',
          overflowX: 'auto',
        }}
      >
        {TABS.map((tab) => {
          const active = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--color-border)',
                background: active ? 'var(--color-primary)' : 'transparent',
                color: active ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
                fontSize: 'var(--text-meta)',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {t.t(`dashboard.catalog.tabs.${tab.id}`)}
            </button>
          );
        })}
      </nav>
    </header>
  );
}

// ── PACKAGES TAB ───────────────────────────────────────────────────────

function PackagesTab({
  tenantId,
  outlets,
}: {
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
}) {
  const [reloadTick, setReloadTick] = useState(0);
  const subscribe = useMemo(() => {
    const repo = packagesRepoFor(tenantId);
    return (cb: (vs: Package[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const {
    data: packages,
    loading,
    error,
  } = useTrackedSnapshot<Package[]>(['packages', tenantId, reloadTick], subscribe);

  // Sprint 106 — vehicle types are tenant-scoped now; one subscription for
  // the whole tenant covers every row's pricing matrix.
  const vtSubscribe = useMemo(() => {
    const repo = vehicleTypesRepoFor(tenantId);
    return (cb: (vs: VehicleType[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: vehicleTypes } = useTrackedSnapshot<VehicleType[]>(
    ['vehicle-types-for-packages', tenantId],
    vtSubscribe,
  );

  if (loading) return <SkeletonRows />;
  if (error)
    return <ErrorCard message={error.message} onRetry={() => setReloadTick((n) => n + 1)} />;
  if (!packages || packages.length === 0) return <EmptyCard kind="packages" />;

  const activeVehicleTypes = (vehicleTypes ?? []).filter((v) => v.active !== false);
  const sorted = packages.slice().sort((a, b) => (a.tierRank ?? 0) - (b.tierRank ?? 0));

  return (
    <div style={sectionGridStyle}>
      {sorted.map((pkg) => (
        <PackageRow
          key={pkg.id}
          pkg={pkg}
          tenantId={tenantId}
          vehicleTypes={activeVehicleTypes}
          outlets={outlets}
        />
      ))}
    </div>
  );
}

function PackageRow({
  pkg,
  tenantId,
  vehicleTypes,
  outlets,
}: {
  readonly pkg: Package;
  readonly tenantId: string;
  readonly vehicleTypes: readonly VehicleType[];
  readonly outlets: readonly OutletLite[];
}) {
  const t = useI18n();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(pkg.name['en'] ?? pkg.id);
  const [active, setActive] = useState(pkg.active);
  const [featured, setFeatured] = useState(pkg.featured);
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      vehicleTypes.map((vt) => [vt.id, senToInputRm(pkg.pricingByVehicleType[vt.id])]),
    ),
  );
  // Sprint 106 — per-outlet pricing overrides cover EVERY outlet (the
  // tenant-default matrix is the fallback for any outlet without an
  // explicit override). The `enabled` flag tracks whether the operator
  // has chosen to override at this outlet.
  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const [overrides, setOverrides] = useState<
    Record<string, { enabled: boolean; prices: Record<string, string> }>
  >(() =>
    Object.fromEntries(
      outletIds.map((oid) => {
        const stored = pkg.outletPriceOverrides?.[oid] ?? {};
        const hasAny = Object.keys(stored).length > 0;
        return [
          oid,
          {
            enabled: hasAny,
            prices: Object.fromEntries(
              vehicleTypes.map((vt) => [vt.id, senToInputRm(stored[vt.id])]),
            ),
          },
        ];
      }),
    ),
  );
  const [outletAssignment, setOutletAssignment] = useState<Record<string, boolean>>(() =>
    outletAssignmentMap(pkg.excludedOutletIds, outletIds),
  );
  const [stage, setStage] = useState<RowStage>({ kind: 'idle' });
  // Anti-undercut guard — non-null while the low-price confirmation is open.
  const [pendingFlags, setPendingFlags] = useState<LowPriceFlag[] | null>(null);

  useEffect(() => {
    if (editing) return;
    setName(pkg.name['en'] ?? pkg.id);
    setActive(pkg.active);
    setFeatured(pkg.featured);
    setPrices(
      Object.fromEntries(
        vehicleTypes.map((vt) => [vt.id, senToInputRm(pkg.pricingByVehicleType[vt.id])]),
      ),
    );
    setOverrides(
      Object.fromEntries(
        outletIds.map((oid) => {
          const stored = pkg.outletPriceOverrides?.[oid] ?? {};
          const hasAny = Object.keys(stored).length > 0;
          return [
            oid,
            {
              enabled: hasAny,
              prices: Object.fromEntries(
                vehicleTypes.map((vt) => [vt.id, senToInputRm(stored[vt.id])]),
              ),
            },
          ];
        }),
      ),
    );
    setOutletAssignment(outletAssignmentMap(pkg.excludedOutletIds, outletIds));
  }, [editing, pkg, vehicleTypes, outletIds]);

  function toggleOutletAssignment(outletId: string) {
    setOutletAssignment((prev) => ({ ...prev, [outletId]: !(prev[outletId] ?? true) }));
  }

  const visibleCount = Object.values(outletAssignment).filter((v) => v).length;
  const allUnchecked = outlets.length > 0 && visibleCount === 0;
  const excludedAtTenant = pkg.excludedOutletIds.filter((id) => outlets.some((o) => o.id === id));
  const assignmentBadge =
    outlets.length === 0
      ? null
      : excludedAtTenant.length === 0
        ? outlets.length === 1
          ? null
          : t.t('dashboard.catalog.badges.availableAllOutlets', { count: outlets.length })
        : t.t('dashboard.catalog.badges.availableSomeOutlets', {
            shown: outlets.length - excludedAtTenant.length,
            total: outlets.length,
          });

  const overrideOutletsConfigured = Object.values(pkg.outletPriceOverrides ?? {}).filter(
    (m) => Object.keys(m).length > 0,
  ).length;

  // Anti-undercut guard: gather the entered prices (tenant-default + any
  // enabled per-outlet override) with their currently-stored counterparts,
  // so the low-price check can compare new vs current for each vehicle type.
  function lowPriceEntries(): LowPriceEntry[] {
    const itemName = name.trim() || pkg.name['en'] || pkg.id;
    const entries: LowPriceEntry[] = [];
    for (const vt of vehicleTypes) {
      const sen = inputRmToSen(prices[vt.id] ?? '');
      if (sen === null || sen <= 0) continue;
      const currentSen = pkg.pricingByVehicleType[vt.id];
      entries.push({
        id: `default:${vt.id}`,
        label: `${itemName} · ${vt.name['en']}`,
        newSen: sen,
        ...(currentSen !== undefined ? { currentSen } : {}),
      });
    }
    for (const oid of outletIds) {
      const draft = overrides[oid];
      if (!draft?.enabled) continue;
      const outletName = outlets.find((o) => o.id === oid)?.name ?? oid;
      for (const vt of vehicleTypes) {
        const raw = draft.prices[vt.id] ?? '';
        if (raw.trim() === '') continue;
        const sen = inputRmToSen(raw);
        if (sen === null || sen <= 0) continue;
        const currentSen = pkg.outletPriceOverrides?.[oid]?.[vt.id];
        entries.push({
          id: `override:${oid}:${vt.id}`,
          label: `${itemName} · ${vt.name['en']} · ${outletName}`,
          newSen: sen,
          ...(currentSen !== undefined ? { currentSen } : {}),
        });
      }
    }
    return entries;
  }

  // Save button handler: gate the existing write behind a confirmation when
  // any entered price is unusually low; otherwise commit straight through.
  async function save() {
    const flags = flagLowPrices(lowPriceEntries(), LOW_PRICE_FLOOR_SEN);
    if (flags.length > 0) {
      setPendingFlags(flags);
      return;
    }
    await commitSave();
  }

  function confirmLowPrice() {
    setPendingFlags(null);
    void commitSave();
  }

  async function commitSave() {
    setStage({ kind: 'saving' });
    try {
      const pricingByVehicleType: Record<string, number> = {};
      for (const vt of vehicleTypes) {
        const raw = prices[vt.id] ?? '';
        const sen = inputRmToSen(raw);
        if (sen === null) {
          setStage({
            kind: 'failed',
            message: t.t('dashboard.catalog.errors.invalidPrice', { vehicle: vt.name['en'] }),
          });
          return;
        }
        if (sen > 0) pricingByVehicleType[vt.id] = sen;
      }
      if (allUnchecked) {
        setStage({
          kind: 'failed',
          message: t.t('dashboard.catalog.errors.pickOutletPackage'),
        });
        return;
      }
      const outletPriceOverrides: Record<string, Record<string, number>> = {};
      for (const oid of outletIds) {
        const draft = overrides[oid];
        if (!draft?.enabled) continue;
        const map: Record<string, number> = {};
        for (const vt of vehicleTypes) {
          const raw = draft.prices[vt.id] ?? '';
          if (raw.trim() === '') continue;
          const sen = inputRmToSen(raw);
          if (sen === null) {
            const outletName = outlets.find((o) => o.id === oid)?.name ?? oid;
            setStage({
              kind: 'failed',
              message: t.t('dashboard.catalog.errors.invalidOverridePrice', {
                outlet: outletName,
                vehicle: vt.name['en'],
              }),
            });
            return;
          }
          map[vt.id] = sen;
        }
        if (Object.keys(map).length > 0) {
          outletPriceOverrides[oid] = map;
        }
      }
      const excludedOutletIds = excludedOutletIdsFromMap(outletAssignment, outletIds);
      const repo = packagesRepoFor(tenantId);
      await repo.update(pkg.id, {
        'name.en': name.trim(),
        active,
        featured,
        pricingByVehicleType,
        outletPriceOverrides,
        excludedOutletIds,
        pricingVersion: (pkg.pricingVersion ?? 0) + 1,
        updatedAt: Timestamp.now(),
      });
      logger.info('package updated', { packageId: pkg.id });
      setStage({ kind: 'saved' });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        setEditing(false);
      }, 1000);
    } catch (e) {
      const msg = (e as Error).message ?? t.t('dashboard.catalog.errors.updateFailed');
      setStage({ kind: 'failed', message: msg });
      logger.error('package update failed', { packageId: pkg.id, error: msg });
    }
  }

  // Tenant-default preview line — the price every outlet sees unless
  // the operator has set a per-outlet override below. Override drift is
  // surfaced via the "N outlets with custom pricing" meta below.
  const previewParts = vehicleTypes
    .map((vt) => {
      const price = pkg.pricingByVehicleType[vt.id];
      if (price === undefined) return null;
      return `${vt.name['en']} ${money.format(price)}`;
    })
    .filter((s): s is string => s !== null);

  return (
    <Surface as="article">
      <div style={rowHeaderStyle}>
        <div>
          <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {pkg.name['en'] ?? pkg.id}
            {pkg.featured ? <FeaturedPill /> : null}
            {!pkg.active ? <InactivePill /> : null}
          </p>
          <p style={metaStyle}>
            {t.t('dashboard.catalog.packages.metaLine', {
              count: pkg.includedServiceIds.length,
              duration: pkg.estimatedDurationMin,
              prices: previewParts.join(' · '),
            })}
          </p>
          {assignmentBadge || overrideOutletsConfigured > 0 ? (
            <p style={{ ...metaStyle, marginTop: 'var(--space-1)' }}>
              {assignmentBadge}
              {assignmentBadge && overrideOutletsConfigured > 0 ? ' · ' : ''}
              {overrideOutletsConfigured > 0
                ? t.t(
                    overrideOutletsConfigured === 1
                      ? 'dashboard.catalog.packages.customPricingCountOne'
                      : 'dashboard.catalog.packages.customPricingCountOther',
                    { count: overrideOutletsConfigured },
                  )
                : ''}
            </p>
          ) : null}
        </div>
        <button type="button" onClick={() => setEditing((e) => !e)} style={editButtonStyle}>
          {editing ? t.t('common.cancel') : t.t('dashboard.catalog.edit')}
        </button>
      </div>

      {editing ? (
        <div style={editFormStyle}>
          <FieldRow label={t.t('dashboard.catalog.fields.displayNameEn')}>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
          </FieldRow>
          <FieldRow label={t.t('dashboard.catalog.fields.defaultPricing')}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
                gap: 'var(--space-3)',
              }}
            >
              {vehicleTypes.map((vt) => (
                <label key={vt.id} style={{ display: 'grid', gap: 'var(--space-1)' }}>
                  <span style={subFieldLabel}>{vt.name['en']}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span
                      style={{
                        color: 'var(--color-on-surface-muted)',
                        fontSize: 'var(--text-meta)',
                      }}
                    >
                      RM
                    </span>
                    <input
                      style={{ ...inputStyle, textAlign: 'right' }}
                      inputMode="decimal"
                      value={prices[vt.id] ?? ''}
                      onChange={(e) => setPrices((prev) => ({ ...prev, [vt.id]: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </label>
              ))}
            </div>
          </FieldRow>
          {outlets.length > 1 ? (
            <FieldRow label={t.t('dashboard.catalog.fields.availableAt')}>
              <OutletCheckboxGrid
                outlets={outlets}
                assignment={outletAssignment}
                onToggle={toggleOutletAssignment}
                allUnchecked={allUnchecked}
              />
            </FieldRow>
          ) : null}
          {outlets.length > 1 ? (
            <FieldRow label={t.t('dashboard.catalog.fields.perOutletOverrides')}>
              <PerOutletOverrideEditor
                outlets={outlets}
                vehicleTypes={vehicleTypes}
                overrides={overrides}
                onToggleOutlet={(oid) =>
                  setOverrides((prev) => ({
                    ...prev,
                    [oid]: {
                      enabled: !(prev[oid]?.enabled ?? false),
                      prices: prev[oid]?.prices ?? {},
                    },
                  }))
                }
                onPriceChange={(oid, vtId, value) =>
                  setOverrides((prev) => ({
                    ...prev,
                    [oid]: {
                      enabled: prev[oid]?.enabled ?? false,
                      prices: { ...(prev[oid]?.prices ?? {}), [vtId]: value },
                    },
                  }))
                }
              />
            </FieldRow>
          ) : null}
          <FieldRow label="">
            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
              <CheckboxField
                label={t.t('dashboard.catalog.fields.active')}
                checked={active}
                onChange={setActive}
              />
              <CheckboxField
                label={t.t('dashboard.catalog.fields.featured')}
                checked={featured}
                onChange={setFeatured}
              />
            </div>
          </FieldRow>
          <SaveBar stage={stage} onSave={save} />
        </div>
      ) : null}
      {pendingFlags ? (
        <LowPriceConfirmDialog
          flags={pendingFlags}
          onConfirm={confirmLowPrice}
          onCancel={() => setPendingFlags(null)}
        />
      ) : null}
    </Surface>
  );
}

/**
 * Sprint 105 — per-outlet pricing override editor for packages. Each
 * non-active outlet gets a collapsible row; on enable, the operator sees
 * the matrix for THIS outlet only. Blank cells mean "fall back to tenant
 * default" — kept blank rather than seeded with the default so the
 * operator sees exactly which prices they've chosen to override.
 */
function PerOutletOverrideEditor({
  outlets,
  vehicleTypes,
  overrides,
  onToggleOutlet,
  onPriceChange,
}: {
  readonly outlets: readonly OutletLite[];
  readonly vehicleTypes: readonly VehicleType[];
  readonly overrides: Readonly<
    Record<string, { enabled: boolean; prices: Record<string, string> }>
  >;
  readonly onToggleOutlet: (outletId: string) => void;
  readonly onPriceChange: (outletId: string, vehicleTypeId: string, value: string) => void;
}) {
  const t = useI18n();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t.t('dashboard.catalog.packages.overrideHint')}
      </p>
      {outlets.map((o) => {
        const draft = overrides[o.id];
        const enabled = draft?.enabled ?? false;
        return (
          <div
            key={o.id}
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-2)',
              padding: 'var(--space-3)',
              background: enabled ? 'rgba(99,102,241,0.04)' : 'transparent',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input type="checkbox" checked={enabled} onChange={() => onToggleOutlet(o.id)} />
              <span style={{ fontSize: 'var(--text-meta)', fontWeight: 500 }}>
                {t.t('dashboard.catalog.packages.overrideAtOutlet', { outlet: o.name })}
              </span>
            </label>
            {enabled ? (
              <div
                style={{
                  marginTop: 'var(--space-2)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
                  gap: 'var(--space-2)',
                }}
              >
                {vehicleTypes.map((vt) => (
                  <label key={vt.id} style={{ display: 'grid', gap: 'var(--space-1)' }}>
                    <span style={subFieldLabel}>{vt.name['en']}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                      <span
                        style={{
                          color: 'var(--color-on-surface-muted)',
                          fontSize: 'var(--text-meta)',
                        }}
                      >
                        RM
                      </span>
                      <input
                        style={{ ...inputStyle, textAlign: 'right' }}
                        inputMode="decimal"
                        value={draft?.prices[vt.id] ?? ''}
                        onChange={(e) => onPriceChange(o.id, vt.id, e.target.value)}
                        placeholder={t.t('dashboard.catalog.overridePlaceholder')}
                      />
                    </div>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ── SERVICES TAB ───────────────────────────────────────────────────────

function ServicesTab({
  tenantId,
  outlets,
}: {
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
}) {
  const [reloadTick, setReloadTick] = useState(0);
  const subscribe = useMemo(() => {
    const repo = servicesRepoFor(tenantId);
    return (cb: (vs: Service[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const {
    data: services,
    loading,
    error,
  } = useTrackedSnapshot<Service[]>(['services', tenantId, reloadTick], subscribe);

  if (loading) return <SkeletonRows />;
  if (error)
    return <ErrorCard message={error.message} onRetry={() => setReloadTick((n) => n + 1)} />;
  if (!services || services.length === 0) return <EmptyCard kind="services" />;

  const sorted = services
    .slice()
    .sort((a, b) => (a.name['en'] ?? '').localeCompare(b.name['en'] ?? ''));

  return (
    <div style={sectionGridStyle}>
      {sorted.map((s) => (
        <ServiceRow key={s.id} service={s} tenantId={tenantId} outlets={outlets} />
      ))}
    </div>
  );
}

function ServiceRow({
  service,
  tenantId,
  outlets,
}: {
  readonly service: Service;
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
}) {
  const t = useI18n();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(service.name['en'] ?? service.id);
  const [duration, setDuration] = useState(String(service.durationMin));
  const [active, setActive] = useState(service.active);
  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const [outletAssignment, setOutletAssignment] = useState<Record<string, boolean>>(() =>
    outletAssignmentMap(service.excludedOutletIds, outletIds),
  );
  const [stage, setStage] = useState<RowStage>({ kind: 'idle' });

  useEffect(() => {
    if (editing) return;
    setName(service.name['en'] ?? service.id);
    setDuration(String(service.durationMin));
    setActive(service.active);
    setOutletAssignment(outletAssignmentMap(service.excludedOutletIds, outletIds));
  }, [editing, service, outletIds]);

  function toggleOutletAssignment(outletId: string) {
    setOutletAssignment((prev) => ({ ...prev, [outletId]: !(prev[outletId] ?? true) }));
  }

  const visibleCount = Object.values(outletAssignment).filter((v) => v).length;
  const allUnchecked = outlets.length > 0 && visibleCount === 0;
  const excludedAtTenant = service.excludedOutletIds.filter((id) =>
    outlets.some((o) => o.id === id),
  );
  const assignmentBadge =
    outlets.length === 0
      ? null
      : excludedAtTenant.length === 0
        ? outlets.length === 1
          ? t.t('dashboard.catalog.badges.availableEverywhere')
          : t.t('dashboard.catalog.badges.availableAllOutlets', { count: outlets.length })
        : t.t('dashboard.catalog.badges.availableSomeOutlets', {
            shown: outlets.length - excludedAtTenant.length,
            total: outlets.length,
          });

  async function save() {
    setStage({ kind: 'saving' });
    try {
      const durationMin = Number.parseInt(duration, 10);
      if (!Number.isFinite(durationMin) || durationMin <= 0) {
        setStage({
          kind: 'failed',
          message: t.t('dashboard.catalog.errors.durationPositive'),
        });
        return;
      }
      if (allUnchecked) {
        setStage({
          kind: 'failed',
          message: t.t('dashboard.catalog.errors.pickOutletService'),
        });
        return;
      }
      const excludedOutletIds = excludedOutletIdsFromMap(outletAssignment, outletIds);
      const repo = servicesRepoFor(tenantId);
      await repo.update(service.id, {
        'name.en': name.trim(),
        durationMin,
        active,
        excludedOutletIds,
      });
      logger.info('service updated', { serviceId: service.id });
      setStage({ kind: 'saved' });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        setEditing(false);
      }, 1000);
    } catch (e) {
      setStage({
        kind: 'failed',
        message: (e as Error).message ?? t.t('dashboard.catalog.errors.updateFailed'),
      });
    }
  }

  return (
    <Surface as="article">
      <div style={rowHeaderStyle}>
        <div>
          <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {service.name['en'] ?? service.id}
            {!service.active ? <InactivePill /> : null}
          </p>
          <p style={metaStyle}>
            {t.t('dashboard.catalog.services.durationMin', { duration: service.durationMin })}
            {assignmentBadge ? ` · ${assignmentBadge}` : ''}
          </p>
        </div>
        <button type="button" onClick={() => setEditing((e) => !e)} style={editButtonStyle}>
          {editing ? t.t('common.cancel') : t.t('dashboard.catalog.edit')}
        </button>
      </div>
      {editing ? (
        <div style={editFormStyle}>
          <FieldRow label={t.t('dashboard.catalog.fields.displayNameEn')}>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
          </FieldRow>
          <FieldRow label={t.t('dashboard.catalog.fields.durationMinutes')}>
            <input
              style={{ ...inputStyle, maxWidth: 120 }}
              inputMode="numeric"
              value={duration}
              onChange={(e) => setDuration(e.target.value.replace(/\D/g, ''))}
            />
          </FieldRow>
          {outlets.length > 1 ? (
            <FieldRow label={t.t('dashboard.catalog.fields.availableAt')}>
              <OutletCheckboxGrid
                outlets={outlets}
                assignment={outletAssignment}
                onToggle={toggleOutletAssignment}
                allUnchecked={allUnchecked}
              />
            </FieldRow>
          ) : null}
          <FieldRow label="">
            <CheckboxField
              label={t.t('dashboard.catalog.fields.active')}
              checked={active}
              onChange={setActive}
            />
          </FieldRow>
          <SaveBar stage={stage} onSave={save} />
        </div>
      ) : null}
    </Surface>
  );
}

/**
 * Sprint 104 — shared per-outlet checkbox grid for the smart-default
 * outlet-assignment UX. Used by Service + AddOn rows; will be used by
 * Package + VehicleType rows when those migrate (Sprints 105/106).
 */
function OutletCheckboxGrid({
  outlets,
  assignment,
  onToggle,
  allUnchecked,
}: {
  readonly outlets: readonly OutletLite[];
  readonly assignment: Readonly<Record<string, boolean>>;
  readonly onToggle: (outletId: string) => void;
  readonly allUnchecked: boolean;
}) {
  const t = useI18n();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {t.t('dashboard.catalog.availableHint')}
      </p>
      {outlets.map((o) => {
        const checked = assignment[o.id] ?? true;
        return (
          <label
            key={o.id}
            style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
          >
            <input type="checkbox" checked={checked} onChange={() => onToggle(o.id)} />
            <span style={{ fontSize: 'var(--text-meta)' }}>{o.name}</span>
          </label>
        );
      })}
      {allUnchecked ? (
        <p role="alert" style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#b45309' }}>
          {t.t('dashboard.catalog.pickOneOutletWarning')}
        </p>
      ) : null}
    </div>
  );
}

// ── ADD-ONS TAB ────────────────────────────────────────────────────────

function AddOnsTab({
  tenantId,
  outlets,
}: {
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
}) {
  const [reloadTick, setReloadTick] = useState(0);
  const subscribe = useMemo(() => {
    const repo = addOnsRepoFor(tenantId);
    return (cb: (vs: AddOn[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const {
    data: addOns,
    loading,
    error,
  } = useTrackedSnapshot<AddOn[]>(['addons', tenantId, reloadTick], subscribe);

  // Sprint 106 — tenant-scoped vehicleTypes; one subscription covers
  // every add-on row's pricing matrix.
  const vtSubscribe = useMemo(() => {
    const repo = vehicleTypesRepoFor(tenantId);
    return (cb: (vs: VehicleType[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: vehicleTypes } = useTrackedSnapshot<VehicleType[]>(
    ['vehicle-types-for-addons', tenantId],
    vtSubscribe,
  );

  if (loading) return <SkeletonRows />;
  if (error)
    return <ErrorCard message={error.message} onRetry={() => setReloadTick((n) => n + 1)} />;
  if (!addOns || addOns.length === 0) return <EmptyCard kind="addOns" />;

  const activeVehicleTypes = (vehicleTypes ?? []).filter((v) => v.active !== false);

  return (
    <div style={sectionGridStyle}>
      {addOns.map((a) => (
        <AddOnRow
          key={a.id}
          addOn={a}
          tenantId={tenantId}
          vehicleTypes={activeVehicleTypes}
          outlets={outlets}
        />
      ))}
    </div>
  );
}

function AddOnRow({
  addOn,
  tenantId,
  vehicleTypes,
  outlets,
}: {
  readonly addOn: AddOn;
  readonly tenantId: string;
  readonly vehicleTypes: readonly VehicleType[];
  readonly outlets: readonly OutletLite[];
}) {
  const t = useI18n();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(addOn.name['en'] ?? addOn.id);
  const [active, setActive] = useState(addOn.active);
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      vehicleTypes.map((vt) => [vt.id, senToInputRm(addOn.pricingByVehicleType[vt.id])]),
    ),
  );
  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const [outletAssignment, setOutletAssignment] = useState<Record<string, boolean>>(() =>
    outletAssignmentMap(addOn.excludedOutletIds, outletIds),
  );
  const [stage, setStage] = useState<RowStage>({ kind: 'idle' });
  // Anti-undercut guard — non-null while the low-price confirmation is open.
  const [pendingFlags, setPendingFlags] = useState<LowPriceFlag[] | null>(null);

  useEffect(() => {
    if (editing) return;
    setName(addOn.name['en'] ?? addOn.id);
    setActive(addOn.active);
    setPrices(
      Object.fromEntries(
        vehicleTypes.map((vt) => [vt.id, senToInputRm(addOn.pricingByVehicleType[vt.id])]),
      ),
    );
    setOutletAssignment(outletAssignmentMap(addOn.excludedOutletIds, outletIds));
  }, [editing, addOn, vehicleTypes, outletIds]);

  function toggleOutletAssignment(outletId: string) {
    setOutletAssignment((prev) => ({ ...prev, [outletId]: !(prev[outletId] ?? true) }));
  }

  const visibleCount = Object.values(outletAssignment).filter((v) => v).length;
  const allUnchecked = outlets.length > 0 && visibleCount === 0;
  const excludedAtTenant = addOn.excludedOutletIds.filter((id) => outlets.some((o) => o.id === id));
  const assignmentBadge =
    outlets.length === 0
      ? null
      : excludedAtTenant.length === 0
        ? outlets.length === 1
          ? t.t('dashboard.catalog.badges.availableEverywhere')
          : t.t('dashboard.catalog.badges.availableAllOutlets', { count: outlets.length })
        : t.t('dashboard.catalog.badges.availableSomeOutlets', {
            shown: outlets.length - excludedAtTenant.length,
            total: outlets.length,
          });

  // Anti-undercut guard: gather the entered add-on prices with their
  // currently-stored counterparts for the low-price check (add-ons have no
  // per-outlet overrides, so only the tenant-default matrix is compared).
  function lowPriceEntries(): LowPriceEntry[] {
    const itemName = name.trim() || addOn.name['en'] || addOn.id;
    const entries: LowPriceEntry[] = [];
    for (const vt of vehicleTypes) {
      const sen = inputRmToSen(prices[vt.id] ?? '');
      if (sen === null || sen <= 0) continue;
      const currentSen = addOn.pricingByVehicleType[vt.id];
      entries.push({
        id: `default:${vt.id}`,
        label: `${itemName} · ${vt.name['en']}`,
        newSen: sen,
        ...(currentSen !== undefined ? { currentSen } : {}),
      });
    }
    return entries;
  }

  // Save button handler: gate the existing write behind a confirmation when
  // any entered price is unusually low; otherwise commit straight through.
  async function save() {
    const flags = flagLowPrices(lowPriceEntries(), LOW_PRICE_FLOOR_SEN);
    if (flags.length > 0) {
      setPendingFlags(flags);
      return;
    }
    await commitSave();
  }

  function confirmLowPrice() {
    setPendingFlags(null);
    void commitSave();
  }

  async function commitSave() {
    setStage({ kind: 'saving' });
    try {
      const pricingByVehicleType: Record<string, number> = {};
      for (const vt of vehicleTypes) {
        const raw = prices[vt.id] ?? '';
        const sen = inputRmToSen(raw);
        if (sen === null) {
          setStage({
            kind: 'failed',
            message: t.t('dashboard.catalog.errors.invalidPrice', { vehicle: vt.name['en'] }),
          });
          return;
        }
        if (sen > 0) pricingByVehicleType[vt.id] = sen;
      }
      if (allUnchecked) {
        setStage({
          kind: 'failed',
          message: t.t('dashboard.catalog.errors.pickOutletAddOn'),
        });
        return;
      }
      const excludedOutletIds = excludedOutletIdsFromMap(outletAssignment, outletIds);
      const repo = addOnsRepoFor(tenantId);
      await repo.update(addOn.id, {
        'name.en': name.trim(),
        active,
        pricingByVehicleType,
        excludedOutletIds,
      });
      setStage({ kind: 'saved' });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        setEditing(false);
      }, 1000);
    } catch (e) {
      setStage({
        kind: 'failed',
        message: (e as Error).message ?? t.t('dashboard.catalog.errors.updateFailed'),
      });
    }
  }

  return (
    <Surface as="article">
      <div style={rowHeaderStyle}>
        <div>
          <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {addOn.name['en'] ?? addOn.id}
            {!addOn.active ? <InactivePill /> : null}
          </p>
          <p style={metaStyle}>
            {vehicleTypes
              .filter((vt) => addOn.pricingByVehicleType[vt.id])
              .map(
                (vt) => `${vt.name['en']} ${money.format(addOn.pricingByVehicleType[vt.id] ?? 0)}`,
              )
              .join(' · ') || t.t('dashboard.catalog.addOns.noPricing')}
            {assignmentBadge ? ` · ${assignmentBadge}` : ''}
          </p>
        </div>
        <button type="button" onClick={() => setEditing((e) => !e)} style={editButtonStyle}>
          {editing ? t.t('common.cancel') : t.t('dashboard.catalog.edit')}
        </button>
      </div>
      {editing ? (
        <div style={editFormStyle}>
          <FieldRow label={t.t('dashboard.catalog.fields.displayNameEn')}>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
          </FieldRow>
          <FieldRow label={t.t('dashboard.catalog.fields.pricing')}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
                gap: 'var(--space-3)',
              }}
            >
              {vehicleTypes.map((vt) => (
                <label key={vt.id} style={{ display: 'grid', gap: 'var(--space-1)' }}>
                  <span style={subFieldLabel}>{vt.name['en']}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <span
                      style={{
                        color: 'var(--color-on-surface-muted)',
                        fontSize: 'var(--text-meta)',
                      }}
                    >
                      RM
                    </span>
                    <input
                      style={{ ...inputStyle, textAlign: 'right' }}
                      inputMode="decimal"
                      value={prices[vt.id] ?? ''}
                      onChange={(e) => setPrices((prev) => ({ ...prev, [vt.id]: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </label>
              ))}
            </div>
          </FieldRow>
          {outlets.length > 1 ? (
            <FieldRow label={t.t('dashboard.catalog.fields.availableAt')}>
              <OutletCheckboxGrid
                outlets={outlets}
                assignment={outletAssignment}
                onToggle={toggleOutletAssignment}
                allUnchecked={allUnchecked}
              />
            </FieldRow>
          ) : null}
          <FieldRow label="">
            <CheckboxField
              label={t.t('dashboard.catalog.fields.active')}
              checked={active}
              onChange={setActive}
            />
          </FieldRow>
          <SaveBar stage={stage} onSave={save} />
        </div>
      ) : null}
      {pendingFlags ? (
        <LowPriceConfirmDialog
          flags={pendingFlags}
          onConfirm={confirmLowPrice}
          onCancel={() => setPendingFlags(null)}
        />
      ) : null}
    </Surface>
  );
}

// ── VEHICLE TYPES TAB ──────────────────────────────────────────────────

function VehicleTypesTab({
  tenantId,
  outlets,
}: {
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
}) {
  const [reloadTick, setReloadTick] = useState(0);
  const subscribe = useMemo(() => {
    const repo = vehicleTypesRepoFor(tenantId);
    return (cb: (vs: VehicleType[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const {
    data: vehicleTypes,
    loading,
    error,
  } = useTrackedSnapshot<VehicleType[]>(['vehicle-types', tenantId, reloadTick], subscribe);

  if (loading) return <SkeletonRows />;
  if (error)
    return <ErrorCard message={error.message} onRetry={() => setReloadTick((n) => n + 1)} />;
  if (!vehicleTypes || vehicleTypes.length === 0) return <EmptyCard kind="vehicleTypes" />;

  return (
    <div style={sectionGridStyle}>
      {vehicleTypes.map((v) => (
        <VehicleTypeRow key={v.id} vt={v} tenantId={tenantId} outlets={outlets} />
      ))}
    </div>
  );
}

function VehicleTypeRow({
  vt,
  tenantId,
  outlets,
}: {
  readonly vt: VehicleType;
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
}) {
  const t = useI18n();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(vt.name['en'] ?? vt.id);
  const [active, setActive] = useState(vt.active);
  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const [outletAssignment, setOutletAssignment] = useState<Record<string, boolean>>(() =>
    outletAssignmentMap(vt.excludedOutletIds, outletIds),
  );
  const [stage, setStage] = useState<RowStage>({ kind: 'idle' });

  useEffect(() => {
    if (editing) return;
    setName(vt.name['en'] ?? vt.id);
    setActive(vt.active);
    setOutletAssignment(outletAssignmentMap(vt.excludedOutletIds, outletIds));
  }, [editing, vt, outletIds]);

  function toggleOutletAssignment(outletId: string) {
    setOutletAssignment((prev) => ({ ...prev, [outletId]: !(prev[outletId] ?? true) }));
  }

  const visibleCount = Object.values(outletAssignment).filter((v) => v).length;
  const allUnchecked = outlets.length > 0 && visibleCount === 0;
  const excludedAtTenant = (vt.excludedOutletIds ?? []).filter((id) =>
    outlets.some((o) => o.id === id),
  );
  const assignmentBadge =
    outlets.length === 0 || outlets.length === 1
      ? null
      : excludedAtTenant.length === 0
        ? t.t('dashboard.catalog.badges.availableAllOutlets', { count: outlets.length })
        : t.t('dashboard.catalog.badges.availableSomeOutlets', {
            shown: outlets.length - excludedAtTenant.length,
            total: outlets.length,
          });

  async function save() {
    setStage({ kind: 'saving' });
    try {
      if (allUnchecked) {
        setStage({
          kind: 'failed',
          message: t.t('dashboard.catalog.errors.pickOutletVehicleType'),
        });
        return;
      }
      const excludedOutletIds = excludedOutletIdsFromMap(outletAssignment, outletIds);
      const repo = vehicleTypesRepoFor(tenantId);
      await repo.update(vt.id, {
        'name.en': name.trim(),
        active,
        excludedOutletIds,
      });
      setStage({ kind: 'saved' });
      setTimeout(() => {
        setStage({ kind: 'idle' });
        setEditing(false);
      }, 1000);
    } catch (e) {
      setStage({
        kind: 'failed',
        message: (e as Error).message ?? t.t('dashboard.catalog.errors.updateFailed'),
      });
    }
  }

  return (
    <Surface as="article">
      <div style={rowHeaderStyle}>
        <div>
          <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {vt.name['en'] ?? vt.id}
            {!vt.active ? <InactivePill /> : null}
          </p>
          {assignmentBadge ? <p style={metaStyle}>{assignmentBadge}</p> : null}
        </div>
        <button type="button" onClick={() => setEditing((e) => !e)} style={editButtonStyle}>
          {editing ? t.t('common.cancel') : t.t('dashboard.catalog.edit')}
        </button>
      </div>
      {editing ? (
        <div style={editFormStyle}>
          <FieldRow label={t.t('dashboard.catalog.fields.displayNameEn')}>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
          </FieldRow>
          {outlets.length > 1 ? (
            <FieldRow label={t.t('dashboard.catalog.fields.availableAt')}>
              <OutletCheckboxGrid
                outlets={outlets}
                assignment={outletAssignment}
                onToggle={toggleOutletAssignment}
                allUnchecked={allUnchecked}
              />
            </FieldRow>
          ) : null}
          <FieldRow label="">
            <CheckboxField
              label={t.t('dashboard.catalog.fields.active')}
              checked={active}
              onChange={setActive}
            />
          </FieldRow>
          <SaveBar stage={stage} onSave={save} />
        </div>
      ) : null}
    </Surface>
  );
}

// ── shared primitives ─────────────────────────────────────────────────

type RowStage =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string };

function FeaturedPill() {
  const t = useI18n();
  return (
    <span
      style={{
        marginLeft: 'var(--space-2)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: 'rgba(245,158,11,0.12)',
        color: '#b45309',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        verticalAlign: 'middle',
      }}
    >
      {t.t('dashboard.catalog.featuredPill')}
    </span>
  );
}

function InactivePill() {
  const t = useI18n();
  return (
    <span
      style={{
        marginLeft: 'var(--space-2)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--color-border)',
        color: 'var(--color-on-surface-muted)',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        verticalAlign: 'middle',
      }}
    >
      {t.t('dashboard.catalog.hiddenPill')}
    </span>
  );
}

function FieldRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {label ? <span style={subFieldLabel}>{label}</span> : null}
      {children}
    </div>
  );
}

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--color-primary)' }}
      />
      <span style={{ fontSize: 'var(--text-meta)' }}>{label}</span>
    </label>
  );
}

function SaveBar({ stage, onSave }: { readonly stage: RowStage; readonly onSave: () => void }) {
  const t = useI18n();
  const isSaving = stage.kind === 'saving';
  const isSaved = stage.kind === 'saved';
  const isFailed = stage.kind === 'failed';
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving}
        style={{
          background: isSaving ? 'var(--color-border-strong)' : 'var(--color-primary)',
          color: isSaving ? 'var(--color-on-surface-muted)' : 'var(--color-primary-fg)',
          padding: '8px 18px',
          borderRadius: 'var(--radius-pill)',
          fontWeight: 500,
          fontSize: 'var(--text-meta)',
          border: 'none',
          cursor: isSaving ? 'not-allowed' : 'pointer',
        }}
      >
        {isSaving ? t.t('dashboard.catalog.saving') : t.t('common.save')}
      </button>
      {isSaved ? (
        <span style={{ color: 'var(--color-primary)', fontSize: 'var(--text-meta)' }}>
          {t.t('dashboard.catalog.saved')}
        </span>
      ) : null}
      {isFailed ? (
        <span style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}>
          {stage.message}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Anti-undercut confirmation — shown before a catalog save proceeds when one
 * or more entered prices are unusually low (below the RM 3.00 floor, or a cut
 * of half or more from the current price). Confirm runs the existing save
 * unchanged; Cancel aborts with the form untouched. Follows the dashboard's
 * dialog convention (role="dialog", aria-modal, Esc + backdrop to dismiss) —
 * never `window.confirm` (ADR-0012).
 */
function LowPriceConfirmDialog({
  flags,
  onConfirm,
  onCancel,
}: {
  readonly flags: readonly LowPriceFlag[];
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const t = useI18n();
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.t('dashboard.catalog.lowPrice.title')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        zIndex: 200,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-3)',
          padding: 'var(--space-5)',
          maxWidth: 480,
          width: '100%',
          maxHeight: '90dvh',
          overflowY: 'auto',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {t.t('dashboard.catalog.lowPrice.title')}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t.t('dashboard.catalog.lowPrice.intro')}
        </p>
        <ul
          style={{
            margin: 0,
            paddingLeft: 'var(--space-4)',
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {flags.map((f) => (
            <li key={f.id} style={{ fontSize: 'var(--text-meta)', lineHeight: 1.5 }}>
              {f.reason === 'drop'
                ? t.t('dashboard.catalog.lowPrice.rowDrop', {
                    item: f.label,
                    price: money.format(f.newSen),
                    current: money.format(f.currentSen ?? 0),
                  })
                : t.t('dashboard.catalog.lowPrice.rowFloor', {
                    item: f.label,
                    price: money.format(f.newSen),
                  })}
            </li>
          ))}
        </ul>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button type="button" onClick={onCancel} style={editButtonStyle}>
            {t.t('dashboard.catalog.lowPrice.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              padding: '8px 18px',
              borderRadius: 'var(--radius-pill)',
              fontWeight: 500,
              fontSize: 'var(--text-meta)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t.t('dashboard.catalog.lowPrice.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 80,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
}) {
  const t = useI18n();
  return (
    <Surface as="article">
      <p style={{ margin: 0, color: 'var(--color-accent)' }}>
        {t.t('dashboard.catalog.loadError', { message })}
      </p>
      {onRetry ? (
        <button type="button" onClick={onRetry} style={retryButtonStyle}>
          {t.t('common.tryAgain')}
        </button>
      ) : null}
    </Surface>
  );
}

function EmptyCard({ kind }: { readonly kind: Tab }) {
  const t = useI18n();
  return (
    <Surface as="article">
      <p style={{ margin: 0, fontSize: 'var(--text-body)' }}>
        {t.t('dashboard.catalog.emptyTitle', { kind: t.t(`dashboard.catalog.kinds.${kind}`) })}
      </p>
      <p style={metaStyle}>{t.t('dashboard.catalog.emptyHint')}</p>
    </Surface>
  );
}

function SignedOut() {
  const t = useI18n();
  return (
    <main style={pageStyle}>
      <Surface as="section">
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {t.t('auth.signedOutTitle')}
        </h1>
        <p style={metaStyle}>{t.t('dashboard.catalog.signedOutBody')}</p>
      </Surface>
    </main>
  );
}

function Splash({ label }: { readonly label: string }) {
  return (
    <main style={pageStyle}>
      <Surface as="section">
        <p style={metaStyle}>{label}</p>
      </Surface>
    </main>
  );
}

// ── money helpers ──────────────────────────────────────────────────────

/** Convert sen integer → "12.00" string for input value (empty when 0/undefined). */
function senToInputRm(sen: number | undefined): string {
  if (sen === undefined || sen === null) return '';
  if (sen === 0) return '';
  return (sen / 100).toFixed(2);
}

/** Convert "12.00" / "12" / "12.5" input → sen integer; returns null on garbage. */
function inputRmToSen(raw: string): number | null {
  const s = raw.trim();
  if (s === '') return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const rm = Number.parseFloat(s);
  if (!Number.isFinite(rm) || rm < 0) return null;
  return Math.round(rm * 100);
}

// ── styles ─────────────────────────────────────────────────────────────

const rowHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 'var(--space-3)',
};

const editFormStyle: React.CSSProperties = {
  marginTop: 'var(--space-4)',
  paddingTop: 'var(--space-4)',
  borderTop: '1px solid var(--color-border)',
  display: 'grid',
  gap: 'var(--space-4)',
};

const retryButtonStyle: React.CSSProperties = {
  marginTop: 'var(--space-3)',
  padding: '8px 18px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  border: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};

const editButtonStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  border: '1px solid var(--color-border-strong)',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const subFieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const metaStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
